import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';

export interface MethodForElement {
  method_code: string;
  method_name: string;
  laboratory: string;
  is_accredited: boolean;
  typical_tat_hours?: number;
  description?: string;
  cost_per_sample?: number;
}

export interface ElementMethodMatch {
  element: string;
  availableMethods: MethodForElement[];
  recommendedMethod?: MethodForElement;
}

export interface MultiElementMethod {
  method_code: string;
  method_name: string;
  laboratory: string;
  is_accredited: boolean;
  elements_covered: string[];
  typical_tat_hours: number;
  cost_per_sample: number;
  description: string;
}

export interface MultiElementRecommendation {
  elements: string[];
  multiElementMethod: MultiElementMethod;
  individualMethods: MethodForElement[];
  efficiency: {
    costSavings: number;
    timeSavings: number;
    totalCostReduction: number;
    timeReduction: number;
    methodsReduced: string;
  };
  benefits: string[];
}

export const useElementMethodMatching = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getElementsWithMethods = async (): Promise<string[]> => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.get('/laboratory-methods/elements-with-methods');
      return response as string[];
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load elements';
      setError(errorMessage);
      console.error('Error loading elements with methods:', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const getMethodsForElements = useCallback((elements: string[]): ElementMethodMatch[] => {
    if (!elements || elements.length === 0) {
      return [];
    }

    try {
      // For now, return static data until we implement the full API integration
      // This matches the AFRILAB laboratory method structure from the complete method table
      const elementMethodMap: Record<string, MethodForElement[]> = {
        // GOLD (Au) - 8 methods total (1 accredited)
        'Au': [
          {
            method_code: 'PRE.MO/ANA/001',
            method_name: 'Au by fire assay and AAS finish',
            laboratory: 'LABORATOIRE_AU',
            is_accredited: true,
            typical_tat_hours: 48,
            cost_per_sample: 65.00,
            description: 'Gold analysis by fire assay with atomic absorption finish (ACCREDITED)'
          },
          {
            method_code: 'Au-PRE.MO/ANA/001 TFA ICP',
            method_name: 'Au by fire assay and ICP-AES finish',
            laboratory: 'LABORATOIRE_AU',
            is_accredited: false,
            typical_tat_hours: 48,
            cost_per_sample: 75.00,
            description: 'Gold analysis by fire assay with ICP-AES finish'
          },
          {
            method_code: 'Au-PRE.MO/ANA/001 BLEG ICP',
            method_name: 'Au by BLEG Method and ICP-AES finish',
            laboratory: 'LABORATOIRE_AU',
            is_accredited: false,
            typical_tat_hours: 72,
            cost_per_sample: 85.00,
            description: 'Gold analysis by BLEG method with ICP-AES finish'
          },
          {
            method_code: 'Au-PRE.MO/ANA/001 TC FG',
            method_name: 'Au by aqua regia extraction and AAS Furnace finish',
            laboratory: 'LABORATOIRE_AU',
            is_accredited: false,
            typical_tat_hours: 36,
            cost_per_sample: 55.00,
            description: 'Gold analysis by aqua regia extraction with AAS furnace finish'
          },
          {
            method_code: 'Au-PRE.MO/ANA/001 AR ICP',
            method_name: 'Au by aqua regia extraction with ICP finish',
            laboratory: 'LABORATOIRE_AU',
            is_accredited: false,
            typical_tat_hours: 36,
            cost_per_sample: 65.00,
            description: 'Gold analysis by aqua regia extraction with ICP finish'
          },
          {
            method_code: 'Au-PRE.MO/ANA/001 FA ICP',
            method_name: 'Au by fire assay and ICP finish',
            laboratory: 'LABORATOIRE_AU',
            is_accredited: false,
            typical_tat_hours: 48,
            cost_per_sample: 70.00,
            description: 'Gold analysis by fire assay with ICP finish'
          },
          {
            method_code: 'Au-PRE.MO/ANA/001 GR',
            method_name: 'Au by fire assay and gravimetric finish',
            laboratory: 'LABORATOIRE_AU',
            is_accredited: false,
            typical_tat_hours: 48,
            cost_per_sample: 60.00,
            description: 'Gold analysis by fire assay with gravimetric finish'
          },
          {
            method_code: 'Au-PRE.MO/ANA/001 MS',
            method_name: 'Au by Metallic screening',
            laboratory: 'LABORATOIRE_AU',
            is_accredited: false,
            typical_tat_hours: 24,
            cost_per_sample: 45.00,
            description: 'Gold analysis by metallic screening method'
          }
        ],
        
        // SILVER (Ag) - 2 methods (1 individual + 1 multi-element, both accredited)
        'Ag': [
          {
            method_code: 'PRE.MO/ANA/002',
            method_name: 'Ag Aqua Regia Digestion and with AA finish',
            laboratory: 'LABORATOIRE_SAA',
            is_accredited: true,
            typical_tat_hours: 24,
            cost_per_sample: 45.00,
            description: 'Silver analysis by aqua regia digestion and AA (ACCREDITED)'
          },
          {
            method_code: 'PRE.MO/ANA/002',
            method_name: 'Aqua Regia Digestion and with AA finish (Multi-element)',
            laboratory: 'LABORATOIRE_SAA',
            is_accredited: true,
            typical_tat_hours: 24,
            cost_per_sample: 45.00,
            description: 'Multi-element analysis including Ag by aqua regia digestion (ACCREDITED)'
          },
          {
            method_code: 'PRE.MO/ANA/035',
            method_name: 'Ag by fire assay and gravimetric finish',
            laboratory: 'LABORATOIRE_AU',
            is_accredited: false,
            typical_tat_hours: 48,
            cost_per_sample: 60.00,
            description: 'Silver analysis by fire assay with gravimetric finish'
          }
        ],
        
        // COPPER (Cu) - 6 methods total (2 SAA accredited + 1 ICP accredited + 3 non-accredited)
        'Cu': [
          {
            method_code: 'PRE.MO/ANA/002',
            method_name: 'Cu by Aqua Regia Digestion and with AA finish',
            laboratory: 'LABORATOIRE_SAA',
            is_accredited: true,
            typical_tat_hours: 24,
            cost_per_sample: 75.00, // Increased individual method cost
            description: 'Copper analysis by atomic absorption after aqua regia digestion (ACCREDITED)'
          },
          {
            method_code: 'PRE.MO/ANA/002',
            method_name: 'Multi-element analysis by Atomic Absorption (SAA)',
            laboratory: 'LABORATOIRE_SAA',
            is_accredited: true,
            typical_tat_hours: 24,
            cost_per_sample: 75.00, // Same as individual for Cu
            description: 'Multi-element analysis including Cu by atomic absorption (ACCREDITED)'
          },
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'ICP 55 Four Acid Digestion and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            cost_per_sample: 95.00, // Increased individual ICP cost
            description: 'Comprehensive 55-element analysis including Cu (ACCREDITED)'
          },
          {
            method_code: 'PRE.MO/ANA/037',
            method_name: 'ICP 40 Four Acid Digestion and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: false,
            typical_tat_hours: 48,
            cost_per_sample: 85.00,
            description: '40-element analysis including Cu (not accredited)'
          },
          {
            method_code: 'PRE.MO/ANA/007',
            method_name: 'ICP 32 Alkaline fusion with Sodium Peroxide and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: false,
            typical_tat_hours: 48,
            cost_per_sample: 75.00,
            description: '32-element analysis including Cu (not accredited)'
          },
          {
            method_code: 'VOL-PRE.MO/ANA/001 Cu',
            method_name: 'Cu by Volumetric Titration',
            laboratory: 'LABORATOIRE_VOLUMÉTRIE',
            is_accredited: false,
            typical_tat_hours: 24,
            cost_per_sample: 65.00,
            description: 'Copper analysis by volumetric titration (not accredited)'
          }
        ],
        
        // LEAD (Pb) - 3 methods total (2 SAA accredited + 1 volumetric non-accredited)
        'Pb': [
          {
            method_code: 'PRE.MO/ANA/002',
            method_name: 'Pb by Aqua Regia Digestion and with AA finish',
            laboratory: 'LABORATOIRE_SAA',
            is_accredited: true,
            typical_tat_hours: 24,
            cost_per_sample: 75.00, // Increased individual method cost
            description: 'Lead analysis by atomic absorption after aqua regia digestion (ACCREDITED)'
          },
          {
            method_code: 'PRE.MO/ANA/002',
            method_name: 'Multi-element analysis by Atomic Absorption (SAA)',
            laboratory: 'LABORATOIRE_SAA',
            is_accredited: true,
            typical_tat_hours: 24,
            cost_per_sample: 75.00, // Same as individual
            description: 'Multi-element analysis including Pb by atomic absorption (ACCREDITED)'
          },
          {
            method_code: 'VOL-PRE.MO/ANA/001 Pb',
            method_name: 'Pb by Volumetric Titration',
            laboratory: 'LABORATOIRE_VOLUMÉTRIE',
            is_accredited: false,
            typical_tat_hours: 24,
            cost_per_sample: 65.00,
            description: 'Lead analysis by volumetric titration (not accredited)'
          }
        ],
        
        // ZINC (Zn) - 3 methods total (2 SAA accredited + 1 volumetric non-accredited)
        'Zn': [
          {
            method_code: 'PRE.MO/ANA/002',
            method_name: 'Zn by Aqua Regia Digestion and with AA finish',
            laboratory: 'LABORATOIRE_SAA',
            is_accredited: true,
            typical_tat_hours: 24,
            cost_per_sample: 75.00, // Increased individual method cost
            description: 'Zinc analysis by atomic absorption after aqua regia digestion (ACCREDITED)'
          },
          {
            method_code: 'PRE.MO/ANA/002',
            method_name: 'Multi-element analysis by Atomic Absorption (SAA)',
            laboratory: 'LABORATOIRE_SAA',
            is_accredited: true,
            typical_tat_hours: 24,
            cost_per_sample: 75.00, // Same as individual
            description: 'Multi-element analysis including Zn by atomic absorption (ACCREDITED)'
          },
          {
            method_code: 'VOL-PRE.MO/ANA/001 Zn',
            method_name: 'Zn by Volumetric Titration',
            laboratory: 'LABORATOIRE_VOLUMÉTRIE',
            is_accredited: false,
            typical_tat_hours: 24,
            cost_per_sample: 65.00,
            description: 'Zinc analysis by volumetric titration (not accredited)'
          }
        ],
        
        // IRON (Fe) - Multiple methods
        'Fe': [
          {
            method_code: 'PRE.MO/ANA/002',
            method_name: 'Fe Aqua Regia Digestion and with AA finish',
            laboratory: 'LABORATOIRE_SAA',
            is_accredited: true,
            typical_tat_hours: 24,
            cost_per_sample: 45.00,
            description: 'Iron analysis by aqua regia digestion and AA (ACCREDITED)'
          },
          {
            method_code: 'PRE.MO/ANA/002',
            method_name: 'Aqua Regia Digestion and with AA finish (Multi-element)',
            laboratory: 'LABORATOIRE_SAA',
            is_accredited: true,
            typical_tat_hours: 24,
            cost_per_sample: 45.00,
            description: 'Multi-element analysis including Fe by aqua regia digestion (ACCREDITED)'
          },
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'ICP 55 Four Acid Digestion and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            cost_per_sample: 85.00,
            description: 'Comprehensive 55-element analysis including Fe (ACCREDITED)'
          },
          {
            method_code: 'Fe-PRE.MO/ANA/005',
            method_name: 'Fe by volumetric Titration',
            laboratory: 'LABORATOIRE_VOLUMETRIE',
            is_accredited: false,
            typical_tat_hours: 24,
            cost_per_sample: 35.00,
            description: 'Iron analysis by volumetric titration (not accredited)'
          }
        ],
        
        // MANGANESE (Mn) - Multiple methods
        'Mn': [
          {
            method_code: 'PRE.MO/ANA/002',
            method_name: 'Mn Aqua Regia Digestion and with AA finish',
            laboratory: 'LABORATOIRE_SAA',
            is_accredited: true,
            typical_tat_hours: 24,
            cost_per_sample: 45.00,
            description: 'Manganese analysis by aqua regia digestion and AA (ACCREDITED)'
          },
          {
            method_code: 'PRE.MO/ANA/002',
            method_name: 'Aqua Regia Digestion and with AA finish (Multi-element)',
            laboratory: 'LABORATOIRE_SAA',
            is_accredited: true,
            typical_tat_hours: 24,
            cost_per_sample: 45.00,
            description: 'Multi-element analysis including Mn by aqua regia digestion (ACCREDITED)'
          },
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'ICP 55 Four Acid Digestion and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            cost_per_sample: 85.00,
            description: 'Comprehensive 55-element analysis including Mn (ACCREDITED)'
          },
          {
            method_code: 'Mn-PRE.MO/ANA/007',
            method_name: 'Mn by Volumetric Titration Analysis',
            laboratory: 'LABORATOIRE_VOLUMETRIE',
            is_accredited: false,
            typical_tat_hours: 24,
            cost_per_sample: 35.00,
            description: 'Manganese analysis by volumetric titration (not accredited)'
          }
        ],
        
        // NICKEL (Ni) - SAA methods
        'Ni': [
          {
            method_code: 'PRE.MO/ANA/002',
            method_name: 'Ni Aqua Regia Digestion and with AA finish',
            laboratory: 'LABORATOIRE_SAA',
            is_accredited: true,
            typical_tat_hours: 24,
            cost_per_sample: 45.00,
            description: 'Nickel analysis by aqua regia digestion and AA (ACCREDITED)'
          },
          {
            method_code: 'PRE.MO/ANA/002',
            method_name: 'Aqua Regia Digestion and with AA finish (Multi-element)',
            laboratory: 'LABORATOIRE_SAA',
            is_accredited: true,
            typical_tat_hours: 24,
            cost_per_sample: 45.00,
            description: 'Multi-element analysis including Ni by aqua regia digestion (ACCREDITED)'
          }
        ],
        
        // COBALT (Co) - SAA methods
        'Co': [
          {
            method_code: 'PRE.MO/ANA/002',
            method_name: 'Co Aqua Regia Digestion and with AA finish',
            laboratory: 'LABORATOIRE_SAA',
            is_accredited: true,
            typical_tat_hours: 24,
            cost_per_sample: 45.00,
            description: 'Cobalt analysis by aqua regia digestion and AA (ACCREDITED)'
          },
          {
            method_code: 'PRE.MO/ANA/002',
            method_name: 'Aqua Regia Digestion and with AA finish (Multi-element)',
            laboratory: 'LABORATOIRE_SAA',
            is_accredited: true,
            typical_tat_hours: 24,
            cost_per_sample: 45.00,
            description: 'Multi-element analysis including Co by aqua regia digestion (ACCREDITED)'
          }
        ],
        
        // CADMIUM (Cd) - SAA methods
        'Cd': [
          {
            method_code: 'PRE.MO/ANA/002',
            method_name: 'Cd Aqua Regia Digestion and with AA finish',
            laboratory: 'LABORATOIRE_SAA',
            is_accredited: true,
            typical_tat_hours: 24,
            cost_per_sample: 45.00,
            description: 'Cadmium analysis by aqua regia digestion and AA (ACCREDITED)'
          },
          {
            method_code: 'PRE.MO/ANA/002',
            method_name: 'Aqua Regia Digestion and with AA finish (Multi-element)',
            laboratory: 'LABORATOIRE_SAA',
            is_accredited: true,
            typical_tat_hours: 24,
            cost_per_sample: 45.00,
            description: 'Multi-element analysis including Cd by aqua regia digestion (ACCREDITED)'
          }
        ],
        
        // BARIUM (Ba) - Volumetric method
        'Ba': [
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'ICP 55 Four Acid Digestion and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            cost_per_sample: 85.00,
            description: 'Comprehensive 55-element analysis including Ba (ACCREDITED)'
          },
          {
            method_code: 'Ba-PRE.MO/ANA/016',
            method_name: 'Ba by gravimetric method',
            laboratory: 'LABORATOIRE_VOLUMETRIE',
            is_accredited: false,
            typical_tat_hours: 36,
            cost_per_sample: 40.00,
            description: 'Barium analysis by gravimetric method (not accredited)'
          }
        ],
        
        // MERCURY (Hg) - Special ICP method
        'Hg': [
          {
            method_code: 'D4A-ICP- PRE.MO/ANA/039 Hg',
            method_name: 'ICP Hg Nitric Acid Digestion and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: false,
            typical_tat_hours: 48,
            cost_per_sample: 55.00,
            description: 'Mercury analysis by nitric acid digestion with ICP finish (not accredited)'
          }
        ],
        
        // Major elements - ICP methods
        'Al': [
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'ICP 55 Four Acid Digestion and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            cost_per_sample: 85.00,
            description: 'Comprehensive 55-element analysis including Al (ACCREDITED)'
          },
          {
            method_code: 'PRE.MO/ANA/037',
            method_name: 'ICP 40 Four Acid Digestion and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: false,
            typical_tat_hours: 48,
            cost_per_sample: 75.00,
            description: '40-element analysis including Al (not accredited)'
          },
          {
            method_code: 'PRE.MO/ANA/007',
            method_name: 'ICP 32 Alkaline fusion with Sodium Peroxide and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: false,
            typical_tat_hours: 48,
            cost_per_sample: 70.00,
            description: '32-element analysis including Al by alkaline fusion (not accredited)'
          }
        ],
        
        'Ca': [
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'ICP 55 Four Acid Digestion and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            cost_per_sample: 85.00,
            description: 'Comprehensive 55-element analysis including Ca (ACCREDITED)'
          },
          {
            method_code: 'PRE.MO/ANA/007',
            method_name: 'ICP 32 Alkaline fusion with Sodium Peroxide and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: false,
            typical_tat_hours: 48,
            cost_per_sample: 70.00,
            description: '32-element analysis including Ca by alkaline fusion (not accredited)'
          }
        ],
        
        'Mg': [
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'ICP 55 Four Acid Digestion and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            cost_per_sample: 85.00,
            description: 'Comprehensive 55-element analysis including Mg (ACCREDITED)'
          },
          {
            method_code: 'PRE.MO/ANA/007',
            method_name: 'ICP 32 Alkaline fusion with Sodium Peroxide and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: false,
            typical_tat_hours: 48,
            cost_per_sample: 70.00,
            description: '32-element analysis including Mg by alkaline fusion (not accredited)'
          }
        ],
        
        'Si': [
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'ICP 55 Four Acid Digestion and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            cost_per_sample: 85.00,
            description: 'Comprehensive 55-element analysis including Si (ACCREDITED)'
          }
        ],
        
        'K': [
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'ICP 55 Four Acid Digestion and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            cost_per_sample: 85.00,
            description: 'Comprehensive 55-element analysis including K (ACCREDITED)'
          }
        ],
        
        'Na': [
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'ICP 55 Four Acid Digestion and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            cost_per_sample: 85.00,
            description: 'Comprehensive 55-element analysis including Na (ACCREDITED)'
          }
        ],
        
        'P': [
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'ICP 55 Four Acid Digestion and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            cost_per_sample: 85.00,
            description: 'Comprehensive 55-element analysis including P (ACCREDITED)'
          }
        ],
        
        'S': [
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'ICP 55 Four Acid Digestion and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            cost_per_sample: 85.00,
            description: 'Comprehensive 55-element analysis including S (ACCREDITED)'
          }
        ],
        
        'Ti': [
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'ICP 55 Four Acid Digestion and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            cost_per_sample: 85.00,
            description: 'Comprehensive 55-element analysis including Ti (ACCREDITED)'
          }
        ],
        
        // Trace elements
        'As': [
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'ICP 55 Four Acid Digestion and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            cost_per_sample: 85.00,
            description: 'Comprehensive 55-element analysis including As (ACCREDITED)'
          }
        ],
        
        'Sb': [
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'ICP 55 Four Acid Digestion and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            cost_per_sample: 85.00,
            description: 'Comprehensive 55-element analysis including Sb (ACCREDITED)'
          }
        ],
        
        'Bi': [
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'ICP 55 Four Acid Digestion and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            cost_per_sample: 85.00,
            description: 'Comprehensive 55-element analysis including Bi (ACCREDITED)'
          }
        ],
        
        'Mo': [
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'ICP 55 Four Acid Digestion and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            cost_per_sample: 85.00,
            description: 'Comprehensive 55-element analysis including Mo (ACCREDITED)'
          }
        ],
        
        'W': [
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'ICP 55 Four Acid Digestion and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            cost_per_sample: 85.00,
            description: 'Comprehensive 55-element analysis including W (ACCREDITED)'
          }
        ],
        
        'V': [
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'ICP 55 Four Acid Digestion and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            cost_per_sample: 85.00,
            description: 'Comprehensive 55-element analysis including V (ACCREDITED)'
          }
        ],
        
        'Cr': [
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'ICP 55 Four Acid Digestion and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            cost_per_sample: 85.00,
            description: 'Comprehensive 55-element analysis including Cr (ACCREDITED)'
          }
        ],
        
        'Sr': [
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'ICP 55 Four Acid Digestion and with ICP finish',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            cost_per_sample: 85.00,
            description: 'Comprehensive 55-element analysis including Sr (ACCREDITED)'
          }
        ],
        
        // Platinum group elements
        'Pt': [
          {
            method_code: 'PGE-PRE.MO/ANA/001',
            method_name: 'PGE by fire assay and ICP-MS finish',
            laboratory: 'LABORATOIRE_AU',
            is_accredited: false,
            typical_tat_hours: 72,
            cost_per_sample: 120.00,
            description: 'Platinum group elements by fire assay with ICP-MS finish (not accredited)'
          }
        ],
        
        'Pd': [
          {
            method_code: 'PGE-PRE.MO/ANA/001',
            method_name: 'PGE by fire assay and ICP-MS finish',
            laboratory: 'LABORATOIRE_AU',
            is_accredited: false,
            typical_tat_hours: 72,
            cost_per_sample: 120.00,
            description: 'Platinum group elements by fire assay with ICP-MS finish (not accredited)'
          }
        ],
        
        // 🆕 COMPOUND ELEMENTS (Oxides, Carbonates, Fluorides)
        'Al2O3': [
          {
            method_code: 'PRE.MO/ANA/007',
            method_name: 'ICP fusion (32 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: false,
            typical_tat_hours: 48,
            description: 'Al2O3 par ICP fusion (0.01%)'
          },
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'Roche ICP - 4acide (40 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            description: 'Al2O3 par ICP 4 acides (0.01%)'
          },
          {
            method_code: 'PRE.MO/ANA/037',
            method_name: 'Stream/Sol ICP - 4acide (40 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            description: 'Al2O3 par ICP 4 acides (0.01%)'
          },
          {
            method_code: 'PRE.MO/ANA/039',
            method_name: 'ICP Majeurs (10 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: false,
            typical_tat_hours: 24,
            description: 'Al2O3 par ICP Majeurs (0.01%)'
          }
        ],
        
        'CaO': [
          {
            method_code: 'PRE.MO/ANA/007',
            method_name: 'ICP fusion (32 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: false,
            typical_tat_hours: 48,
            description: 'CaO par ICP fusion (0.01%)'
          },
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'Roche ICP - 4acide (40 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            description: 'CaO par ICP 4 acides (0.01%)'
          },
          {
            method_code: 'PRE.MO/ANA/039',
            method_name: 'ICP Majeurs (10 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: false,
            typical_tat_hours: 24,
            description: 'CaO par ICP Majeurs (0.01%)'
          }
        ],
        
        'Fe2O3': [
          {
            method_code: 'PRE.MO/ANA/007',
            method_name: 'ICP fusion (32 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: false,
            typical_tat_hours: 48,
            description: 'Fe2O3 par ICP fusion (0.01%)'
          },
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'Roche ICP - 4acide (40 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            description: 'Fe2O3 par ICP 4 acides (0.01%)'
          },
          {
            method_code: 'PRE.MO/ANA/039',
            method_name: 'ICP Majeurs (10 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: false,
            typical_tat_hours: 24,
            description: 'Fe2O3 par ICP Majeurs (0.01%)'
          }
        ],
        
        'K2O': [
          {
            method_code: 'PRE.MO/ANA/007',
            method_name: 'ICP fusion (32 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: false,
            typical_tat_hours: 48,
            description: 'K2O par ICP fusion (0.01%)'
          },
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'Roche ICP - 4acide (40 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            description: 'K2O par ICP 4 acides (0.01%)'
          },
          {
            method_code: 'PRE.MO/ANA/039',
            method_name: 'ICP Majeurs (10 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: false,
            typical_tat_hours: 24,
            description: 'K2O par ICP Majeurs (0.01%)'
          }
        ],
        
        'MgO': [
          {
            method_code: 'PRE.MO/ANA/007',
            method_name: 'ICP fusion (32 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: false,
            typical_tat_hours: 48,
            description: 'MgO par ICP fusion (0.01%)'
          },
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'Roche ICP - 4acide (40 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            description: 'MgO par ICP 4 acides (0.01%)'
          },
          {
            method_code: 'PRE.MO/ANA/039',
            method_name: 'ICP Majeurs (10 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: false,
            typical_tat_hours: 24,
            description: 'MgO par ICP Majeurs (0.01%)'
          }
        ],
        
        'MnO': [
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'Roche ICP - 4acide (40 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            description: 'MnO par ICP 4 acides (0.01%)'
          },
          {
            method_code: 'PRE.MO/ANA/037',
            method_name: 'Stream/Sol ICP - 4acide (40 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            description: 'MnO par ICP 4 acides (0.01%)'
          }
        ],
        
        'Na2O': [
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'Roche ICP - 4acide (40 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            description: 'Na2O par ICP 4 acides (0.01%)'
          },
          {
            method_code: 'PRE.MO/ANA/037',
            method_name: 'Stream/Sol ICP - 4acide (40 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            description: 'Na2O par ICP 4 acides (0.01%)'
          }
        ],
        
        'P2O5': [
          {
            method_code: 'PRE.MO/ANA/007',
            method_name: 'ICP fusion (32 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: false,
            typical_tat_hours: 48,
            description: 'P2O5 par ICP fusion (0.01%)'
          },
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'Roche ICP - 4acide (40 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            description: 'P2O5 par ICP 4 acides (0.01%)'
          },
          {
            method_code: 'PRE.MO/ANA/039',
            method_name: 'ICP Majeurs (10 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: false,
            typical_tat_hours: 24,
            description: 'P2O5 par ICP Majeurs (0.01%)'
          }
        ],
        
        'SiO2': [
          {
            method_code: 'PRE.MO/ANA/007',
            method_name: 'ICP fusion (32 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: false,
            typical_tat_hours: 48,
            description: 'SiO2 par ICP fusion (0.01%)'
          },
          {
            method_code: 'PRE.MO/ANA/039',
            method_name: 'ICP Majeurs (10 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: false,
            typical_tat_hours: 24,
            description: 'SiO2 par ICP Majeurs (0.01%)'
          }
        ],
        
        'TiO2': [
          {
            method_code: 'PRE.MO/ANA/007',
            method_name: 'ICP fusion (32 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: false,
            typical_tat_hours: 48,
            description: 'TiO2 par ICP fusion (0.01%)'
          },
          {
            method_code: 'PRE.MO/ANA/038',
            method_name: 'Roche ICP - 4acide (40 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: true,
            typical_tat_hours: 48,
            description: 'TiO2 par ICP 4 acides (0.01%)'
          },
          {
            method_code: 'PRE.MO/ANA/039',
            method_name: 'ICP Majeurs (10 elements)',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: false,
            typical_tat_hours: 24,
            description: 'TiO2 par ICP Majeurs (0.01%)'
          }
        ],
        
        'CaCO3': [
          {
            method_code: 'PRE.MO/ANA/020',
            method_name: 'CaCO3 par ICP',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: false,
            typical_tat_hours: 24,
            description: 'Carbonate de calcium par ICP (0.01%)'
          }
        ],
        
        'CaF2': [
          {
            method_code: 'PRE.MO/ANA/021',
            method_name: 'CaF2 par ICP',
            laboratory: 'LABORATOIRE_ICP',
            is_accredited: false,
            typical_tat_hours: 24,
            description: 'Fluorure de calcium par ICP (0.01%)'
          }
        ]
      };

      const results: ElementMethodMatch[] = [];

      for (const element of elements) {
        const availableMethods = elementMethodMap[element] || [];
        
        // Set recommended method (prefer accredited, then lowest cost)
        const recommendedMethod = availableMethods
          .sort((a, b) => {
            if (a.is_accredited !== b.is_accredited) {
              return b.is_accredited ? 1 : -1; // Accredited first
            }
            return (a.cost_per_sample || 0) - (b.cost_per_sample || 0); // Then by cost
          })[0];

        results.push({
          element,
          availableMethods,
          recommendedMethod
        });
      }

      return results;
    } catch (err) {
      console.error('Error getting methods for elements:', err);
      setError('Failed to get methods for elements');
      return [];
    }
  }, []);

  const getRecommendedMethods = async (elements: string[]): Promise<MultiElementMethod[]> => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.post('/laboratory-methods/multi-element-recommendations', {
        elements
      });
      return response as MultiElementMethod[];
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to get recommendations';
      setError(errorMessage);
      console.error('Error getting multi-element recommendations:', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const getMultiElementRecommendations = useCallback((selectedElements: string[]): MultiElementRecommendation[] => {
    if (selectedElements.length < 2) {
      return [];
    }

    const recommendations: MultiElementRecommendation[] = [];

    // Real multi-element methods from AFRILAB with accurate pricing based on industry standards
    const multiElementMethods = [
      {
        method_code: 'PRE.MO/ANA/002',
        method_name: 'Multi-element analysis by Atomic Absorption (SAA)',
        laboratory: 'LABORATOIRE_SAA',
        elements_covered: ['Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'],
        is_accredited: true,
        typical_tat_hours: 48,
        cost_per_sample: 155.00, // Based on ALS ICP-OES multi-element scan pricing
        description: 'Comprehensive multi-element analysis by atomic absorption spectroscopy'
      },
      {
        method_code: 'PRE.MO/ANA/038',
        method_name: 'ICP Analysis - 55 elements (ICP 55)',
        laboratory: 'LABORATOIRE_ICP',
        elements_covered: ['Ag', 'Al', 'As', 'Au', 'B', 'Ba', 'Be', 'Bi', 'Ca', 'Cd', 'Ce', 'Co', 'Cr', 'Cu', 'Fe', 'Ga', 'Hf', 'Hg', 'K', 'La', 'Li', 'Mg', 'Mn', 'Mo', 'Na', 'Nb', 'Ni', 'P', 'Pb', 'Pd', 'Pt', 'Re', 'S', 'Sb', 'Sc', 'Se', 'Si', 'Sn', 'Sr', 'Ta', 'Te', 'Th', 'Ti', 'Tl', 'U', 'V', 'W', 'Y', 'Zn', 'Zr', 'Dy', 'Er', 'Eu', 'Gd', 'Ho'],
        is_accredited: true,
        typical_tat_hours: 72,
        cost_per_sample: 470.00, // Based on Actlabs ICP-MS and ICP-OES comprehensive scan
        description: 'Comprehensive 55-element analysis by ICP-MS/OES (ACCREDITED)'
      },
      {
        method_code: 'PRE.MO/ANA/037',
        method_name: 'ICP Analysis - 40 elements (ICP 40)',
        laboratory: 'LABORATOIRE_ICP',
        elements_covered: ['Ag', 'Al', 'As', 'Ba', 'Be', 'Bi', 'Ca', 'Cd', 'Co', 'Cr', 'Cu', 'Fe', 'Ga', 'K', 'La', 'Li', 'Mg', 'Mn', 'Mo', 'Na', 'Nb', 'Ni', 'P', 'Pb', 'S', 'Sb', 'Sc', 'Se', 'Si', 'Sn', 'Sr', 'Ta', 'Te', 'Th', 'Ti', 'Tl', 'U', 'V', 'W', 'Zn'],
        is_accredited: false,
        typical_tat_hours: 60,
        cost_per_sample: 350.00, // Estimated based on element count reduction
        description: '40-element analysis by ICP (not accredited)'
      },
      {
        method_code: 'PRE.MO/ANA/007',
        method_name: 'ICP Analysis - 32 elements (ICP 32)',
        laboratory: 'LABORATOIRE_ICP',
        elements_covered: ['Ag', 'Al', 'As', 'Ba', 'Ca', 'Cd', 'Co', 'Cr', 'Cu', 'Fe', 'K', 'Li', 'Mg', 'Mn', 'Mo', 'Na', 'Ni', 'P', 'Pb', 'S', 'Sb', 'Se', 'Si', 'Sr', 'Ti', 'V', 'Zn', 'B', 'Be', 'Bi', 'Ga', 'La'],
        is_accredited: false,
        typical_tat_hours: 48,
        cost_per_sample: 280.00, // Further reduced for smaller element suite
        description: '32-element analysis by ICP (not accredited)'
      }
    ];

    // Check each multi-element method
    multiElementMethods.forEach((multiMethod, index) => {
      const coveredElements = selectedElements.filter(element => 
        multiMethod.elements_covered.includes(element)
      );

      if (coveredElements.length >= 2) {
        // Calculate individual method costs and time
        const individualMethodsData = coveredElements.map(element => {
          const elementData = getMethodsForElements([element]);
          if (elementData.length === 0) return null;
          
          const methods = elementData[0].availableMethods;
          
          // Compare like with like: if multi-element method is accredited, use accredited individual methods
          // If multi-element is not accredited, use cheapest methods
          let bestMethod: MethodForElement;
          
          if (multiMethod.is_accredited) {
            // For accredited multi-element methods, compare with accredited individual methods
            const accreditedMethods = methods.filter(m => m.is_accredited);
            bestMethod = accreditedMethods.length > 0 
              ? accreditedMethods.sort((a, b) => (a.cost_per_sample || 0) - (b.cost_per_sample || 0))[0]
              : methods.sort((a, b) => (a.cost_per_sample || 0) - (b.cost_per_sample || 0))[0];
          } else {
            // For non-accredited multi-element methods, use cheapest available
            bestMethod = methods.sort((a, b) => (a.cost_per_sample || 0) - (b.cost_per_sample || 0))[0];
          }
          
          return bestMethod;
        }).filter(Boolean) as MethodForElement[];

        if (individualMethodsData.length === coveredElements.length) {
          const totalIndividualCost = individualMethodsData.reduce((sum, method) => sum + (method.cost_per_sample || 0), 0);
          const totalIndividualTime = Math.max(...individualMethodsData.map(method => method.typical_tat_hours || 48));

          // Calculate realistic savings based on actual laboratory economics
          const costSavings = ((totalIndividualCost - multiMethod.cost_per_sample) / totalIndividualCost) * 100;
          const timeSavings = ((totalIndividualTime - multiMethod.typical_tat_hours) / totalIndividualTime) * 100;

          // Only recommend if there are actual savings (even small ones)
          if (costSavings > 0 || timeSavings > 0) {
            recommendations.push({
              elements: coveredElements,
              multiElementMethod: multiMethod,
              individualMethods: individualMethodsData,
              efficiency: {
                costSavings: Math.round(costSavings),
                timeSavings: Math.round(timeSavings),
                totalCostReduction: Math.round(totalIndividualCost - multiMethod.cost_per_sample),
                timeReduction: totalIndividualTime - multiMethod.typical_tat_hours,
                methodsReduced: `${individualMethodsData.length} méthodes → 1 méthode`
              },
              benefits: [
                'Économie de coûts significative',
                'Réduction du temps d\'analyse',
                'Cohérence des résultats',
                'Simplification logistique',
                multiMethod.is_accredited ? 'Méthode accréditée' : 'Méthode non-accréditée'
              ]
            });
          }
        }
      }
    });

    // Sort by total cost savings (descending)
    return recommendations.sort((a, b) => b.efficiency.totalCostReduction - a.efficiency.totalCostReduction);
  }, [getMethodsForElements]);

  return {
    loading,
    error,
    getElementsWithMethods,
    getMethodsForElements,
    getRecommendedMethods,
    getMultiElementRecommendations
  };
}; 